import { useRef } from 'react';
import { useNavigate } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { Button } from '@/kit/Button';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { validateForm } from '@/features/manage/formRules';
import { managementReportingService } from '@/core/services/managementReporting';
import { reportsService } from '@/core/services/reports';
import {
  toIsoDateString,
  type ManagementOption,
  type ManagementPeriodRow,
  type SaveManagementPeriodPayload,
} from '@/core/models/management-reporting.models';
import type { TrialBalanceOption } from '@/core/models/reports.models';
import { trialBalanceMismatchHint, trialBalanceOptionsForPeriod } from './periodTrialBalances';
import {
  confirmDelete,
  guardClient,
  MatButton,
  RequiredWrap,
  snapshotSummary,
  useLiveState,
  useOnInit,
  useSelectedClient,
} from './shared';
import './ManagementPeriods.scss';

/** The management period being added or edited; dates stay as picker values until they are saved. */
interface PeriodForm {
  id: string | null;
  name: string;
  periodType: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- a picker value (dayjs) or the API's date string
  startDate: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  endDate: any;
  trialBalanceId: string | null;
}

interface State {
  dataSource: ManagementPeriodRow[];
  trialBalances: TrialBalanceOption[];
  periodTypes: ManagementOption[];
  showForm: boolean;
  form: PeriodForm;
  generateType: number;
  generateStart: Dayjs | string | null;
  /** A plain text field: a string once it has been edited (Angular kept whatever ngModel held). */
  generateCount: number | string;
  /** A failed Save / Generate marks the missing required fields red. */
  formTried: boolean;
  generateTried: boolean;
  /** In-flight flags: Save and Generate cannot be sent twice. */
  saving: boolean;
  generating: boolean;
}

const emptyForm = (): PeriodForm => ({
  id: null,
  name: '',
  periodType: 0,
  startDate: null,
  endDate: null,
  trialBalanceId: null,
});

/** Management periods of the selected client (Angular TaxreturnManagementPeriodsComponent). */
export function TaxreturnManagementPeriods() {
  const navigate = useNavigate();
  const client = useSelectedClient();
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    trialBalances: [],
    periodTypes: [],
    showForm: false,
    form: emptyForm(),
    generateType: 0,
    generateStart: null,
    generateCount: 12,
    formTried: false,
    generateTried: false,
    saving: false,
    generating: false,
  });
  // #182: the period to snapshot once its trial balance has been saved (set when the camera was used without one).
  const snapshotAfterSaveId = useRef<string | null>(null);

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }
    managementReportingService.getOptions().then(
      (options) => set({ periodTypes: options.periodTypes }),
      (error) => notifyError(error),
    );
    reportsService.getTrialBalances(client.id).then(
      (rows) => set({ trialBalances: rows }),
      (error) => notifyError(error),
    );
    load();
  });

  function load(): void {
    if (!client) {
      return;
    }
    managementReportingService.getManagementPeriods(client.id).then(
      (rows) => set({ dataSource: rows }),
      (error) => notifyError(error),
    );
  }

  const setForm = (patch: Partial<PeriodForm>) => set((current) => ({ form: { ...current.form, ...patch } }));

  function onAdd(): void {
    set({ form: emptyForm(), showForm: true, formTried: false });
  }

  function onEdit(row: ManagementPeriodRow): void {
    set({
      form: {
        id: row.id,
        name: row.name,
        periodType: row.periodType,
        startDate: row.startDate,
        endDate: row.endDate,
        trialBalanceId: row.trialBalanceId,
      },
      showForm: true,
      formTried: false,
    });
  }

  function onCancel(): void {
    snapshotAfterSaveId.current = null;
    set({ showForm: false, form: emptyForm(), formTried: false });
  }

  function onSave(): void {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }

    const form = live.current.form;
    const startDate = toIsoDateString(form.startDate);
    const endDate = toIsoDateString(form.endDate);

    if (!form.name.trim() || !startDate || !endDate) {
      set({ formTried: true });
      validateForm(false);
      return;
    }
    if (endDate < startDate) {
      notifyError('The end date must be on or after the start date.');
      return;
    }

    const payload: SaveManagementPeriodPayload = {
      id: form.id,
      clientId: client.id,
      name: form.name.trim(),
      periodType: form.periodType,
      startDate,
      endDate,
      trialBalanceId: form.trialBalanceId,
    };

    set({ saving: true });
    managementReportingService.saveManagementPeriod(payload).then(
      () => {
        set({ saving: false });
        notifySuccess(NotificationTextMessage.successMessage);
        const snapshotId = snapshotAfterSaveId.current;
        snapshotAfterSaveId.current = null;
        onCancel();
        if (snapshotId && snapshotId === payload.id && payload.trialBalanceId) {
          takeSnapshot(snapshotId, payload.trialBalanceId);
        } else {
          load();
        }
      },
      (error) => {
        set({ saving: false });
        notifyError(error);
      },
    );
  }

  function onGenerate(): void {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }

    const { generateStart, generateType, generateCount } = live.current;
    const startDate = toIsoDateString(generateStart);
    if (!startDate) {
      set({ generateTried: true });
      validateForm(false);
      return;
    }
    // Coercive on purpose: once edited the count is a string (Angular compared it as is).
    const count = generateCount as number;
    if (count < 1 || count > 48) {
      notifyError('Generate between 1 and 48 periods.');
      return;
    }

    set({ generating: true });
    managementReportingService
      .generateManagementPeriods({ clientId: client.id, periodType: generateType, startDate, count })
      .then(
        (result) => {
          set({ dataSource: result.periods, generating: false });
          notifySuccess(
            result.created === 0
              ? 'Every period in that range already exists.'
              : `${result.created} management period(s) created.`,
          );
        },
        (error) => {
          set({ generating: false });
          notifyError(error);
        },
      );
  }

  /** Saves a snapshot of the selected trial balance as the period's periodic trial balance. */
  function onSnapshot(row: ManagementPeriodRow): void {
    if (!row.trialBalanceId) {
      // #182: open the period to choose its trial balance; saving it then takes the snapshot straight away.
      onEdit(row);
      snapshotAfterSaveId.current = row.id;
      notifyError(
        `Choose the trial balance for ${row.name} and click Save - the snapshot is taken as soon as it is saved.`,
      );
      return;
    }
    takeSnapshot(row.id, row.trialBalanceId);
  }

  function takeSnapshot(periodId: string, trialBalanceId: string): void {
    managementReportingService.snapshotManagementPeriod(periodId, trialBalanceId).then(
      (result) => {
        notifySuccess(`Snapshot saved with ${result.lineCount} line(s).`);
        load();
      },
      (error) => notifyError(error),
    );
  }

  function onToggleStatus(row: ManagementPeriodRow): void {
    managementReportingService.setManagementPeriodStatus(row.id, row.status === 1 ? 0 : 1).then(
      () => {
        notifySuccess(row.status === 1 ? 'Management period reopened.' : 'Management period closed.');
        load();
      },
      (error) => notifyError(error),
    );
  }

  async function onDelete(row: ManagementPeriodRow): Promise<void> {
    if (!(await confirmDelete('Management Period'))) {
      return;
    }
    managementReportingService.deleteManagementPeriods([row.id]).then(
      () => {
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
        load();
      },
      (error) => notifyError(error),
    );
  }

  const onOpenReport = (row: ManagementPeriodRow) =>
    navigate({
      pathname: '/home/taxreturns/reports-periodictrialbalance',
      search: `?id=${encodeURIComponent(row.id)}`,
    });

  const onOpenBudget = (row: ManagementPeriodRow) =>
    navigate({ pathname: '/home/taxreturns/reports-budgets', search: `?id=${encodeURIComponent(row.id)}` });

  // APT376: the trial balances show their dates, those for this period's dates come first, and a choice that would be refused says why before Save.
  const formStart = toIsoDateString(s.form.startDate);
  const formEnd = toIsoDateString(s.form.endDate);
  const trialBalanceOptions = trialBalanceOptionsForPeriod(s.trialBalances, formStart, formEnd);
  const mismatchHint = trialBalanceMismatchHint(
    s.trialBalances.find((tb) => tb.id === s.form.trialBalanceId),
    s.form.name,
    formStart,
    formEnd,
  );

  const columns: DataTableColumn<ManagementPeriodRow>[] = [
    { id: 'name', header: 'Period', cell: (row) => row.name },
    { id: 'type', header: 'Type', headerClassName: 'w-150', cell: (row) => row.periodTypeName },
    {
      id: 'dates',
      header: 'Dates',
      headerClassName: 'w-200',
      cell: (row) => `${row.startDate} - ${row.endDate}`,
    },
    {
      id: 'trialbalance',
      // #183: the trial balance chosen for the period's snapshot (Edit); the snapshot copies it when taken.
      header: 'Trial balance for snapshot',
      headerClassName: 'w-150',
      cell: (row) => row.trialBalanceRef || '-',
    },
    {
      id: 'snapshot',
      header: 'Snapshot',
      headerClassName: 'w-200',
      cell: (row) => snapshotSummary(row),
    },
    { id: 'status', header: 'Status', headerClassName: 'w-100', cell: (row) => row.statusName },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-200',
      cell: (row) => (
        <>
          <Tooltip title="Take snapshot">
            <MatButton icon="camera" iconOutlined onClick={() => onSnapshot(row)} />
          </Tooltip>
          <Tooltip title="Periodic reports">
            <MatButton icon="assessment" iconOutlined onClick={() => onOpenReport(row)} />
          </Tooltip>
          <Tooltip title="Budget">
            <MatButton icon="savings" iconOutlined onClick={() => onOpenBudget(row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <MatButton icon="edit" iconOutlined onClick={() => onEdit(row)} />
          </Tooltip>
          <Tooltip title="Open or close">
            <MatButton
              icon={row.status === 1 ? 'lock' : 'lock_open'}
              iconOutlined
              onClick={() => onToggleStatus(row)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <MatButton icon="delete_outline" iconOutlined onClick={() => onDelete(row)} />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-management-periods">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <MatButton className="action-button primary-button" icon="add" onClick={onAdd}>
              <span>Management Period</span>
            </MatButton>
          </div>
          <div className="content-header-right mb-1">
            <Select
              className="w-150"
              items={s.periodTypes}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={s.generateType}
              onChange={(value) => set({ generateType: value as number })}
            />
            <RequiredWrap>
              <DateField
                fieldClassName="w-180"
                readOnly
                placeholder="First period starts"
                invalid={s.generateTried && !toIsoDateString(s.generateStart)}
                value={s.generateStart}
                onChange={(value) => set({ generateStart: value })}
              />
            </RequiredWrap>
            <InputField
              fieldClassName="w-100"
              inputMode="numeric"
              charMode="number"
              value={s.generateCount}
              onChange={(event) => set({ generateCount: event.target.value })}
            />
            <MatButton
              className="action-button secondary-button"
              icon="date_range"
              busy={s.generating}
              onClick={onGenerate}
            >
              <span>Generate</span>
            </MatButton>
          </div>
        </div>
        {s.generateTried && !toIsoDateString(s.generateStart) && (
          <p className="text-danger font-size-12 mb-1">Select the date the first management period starts.</p>
        )}

        {/* #183: how the monthly (or quarterly) figures are produced from a trial balance. */}
        <p className="text-muted font-size-12 mb-1">
          Each management period takes its figures from the trial balance chosen for it (Edit), captured as a snapshot
          with the camera icon. A snapshot copies the trial balance as it is, so use one trial balance for each period
          (its own dates, or a running year-to-date one ending on the period's last day): a trial balance for a whole
          year would show the full-year figures in every month. The periodic trial balance, variance and management packs
          compare those snapshots; take the snapshot again after the trial balance changes.
        </p>

        {s.showForm && (
          <div className="content-header pt-1 mb-0 hr-top">
            <div className="d-flex gap-20 align-items-center flex-wrap">
              <RequiredWrap>
                <InputField
                  fieldClassName="w-270"
                  maxLength={100}
                  placeholder="Period name"
                  charMode="alphanumeric"
                  invalid={s.formTried && !s.form.name.trim()}
                  value={s.form.name}
                  onChange={(event) => setForm({ name: event.target.value })}
                />
              </RequiredWrap>
              <Select
                className="w-150"
                items={s.periodTypes}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                searchable={false}
                value={s.form.periodType}
                onChange={(value) => setForm({ periodType: value as number })}
              />
              <RequiredWrap>
                <DateField
                  fieldClassName="w-180"
                  readOnly
                  placeholder="From"
                  invalid={s.formTried && !formStart}
                  value={s.form.startDate}
                  onChange={(value) => setForm({ startDate: value })}
                />
              </RequiredWrap>
              <RequiredWrap>
                <DateField
                  fieldClassName="w-180"
                  readOnly
                  placeholder="To"
                  min={formStart ? dayjs(formStart) : undefined}
                  invalid={(s.formTried && !formEnd) || dateRangeProblem(formStart, formEnd) !== null}
                  value={s.form.endDate}
                  onChange={(value) => setForm({ endDate: value })}
                />
              </RequiredWrap>
              <Select
                className="w-270"
                items={trialBalanceOptions}
                bindLabel="display"
                bindValue="id"
                groupBy={trialBalanceOptions.some((tb) => tb.group) ? 'group' : undefined}
                placeholder="Trial balance for the snapshot"
                value={s.form.trialBalanceId}
                onChange={(value) => setForm({ trialBalanceId: value as string | null })}
              />
              <Button className="action-button primary-button" busy={s.saving} onClick={onSave}>
                Save
              </Button>
              <Button className="action-button secondary-button" onClick={onCancel}>
                Cancel
              </Button>
            </div>
            {s.formTried && (!s.form.name.trim() || !formStart || !formEnd) && (
              <p className="text-danger font-size-12 mt-1 mb-0">Enter the period name, start date and end date.</p>
            )}
            {mismatchHint && (
              <p className="text-danger font-size-12 mt-1 mb-0" role="alert">
                {mismatchHint}
              </p>
            )}
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(row) => row.id} />
        </div>
      </div>
    </SideMenuPage>
  );
}
